import React, { useState, useEffect, useContext, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import axios from 'axios';
import { 
  Users as UsersIcon, Search, ChevronLeft, ChevronRight, Eye, Edit, Trash2, X, 
  UserPlus, Copy, Shield, User, LogIn, LogOut, Clock, Ban, UserCheck, UserX, 
  AlertTriangle, RefreshCw, ShieldAlert, ShieldCheck, MailCheck, MailX, HelpCircle
} from 'lucide-react';
import CompanyAdminLayout from '@/components/CompanyAdminLayout';
import ConfirmationModal from '@/components/ConfirmationModal';
import { AuthContext } from '@/App';
import { Checkbox } from '@/components/ui/checkbox';
import { toast } from 'sonner';
import { PasswordInput } from '@/components/ui/password-input';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL || '';

const getEmailVerificationStatus = (user) => {
  if (user?.emailVerificationStatus) return user.emailVerificationStatus;
  const value = user?.emailVerified;
  if (value === undefined || value === null) return 'unverified';
  if (typeof value === 'boolean') return value ? 'verified' : 'unverified';
  return 'unknown';
};

const EmailVerificationBadge = ({ status }) => {
  if (status === 'verified') {
    return (
      <span className="inline-flex items-center gap-1 px-2 py-1 rounded-full text-xs font-medium bg-green-100 text-green-700 whitespace-nowrap">
        <MailCheck className="w-3 h-3" /> Verified
      </span>
    );
  }
  if (status === 'unknown') {
    return (
      <span className="inline-flex items-center gap-1 px-2 py-1 rounded-full text-xs font-medium bg-gray-100 text-gray-500 whitespace-nowrap">
        <HelpCircle className="w-3 h-3" /> Unknown
      </span>
    );
  }
  return (
    <span className="inline-flex items-center gap-1 px-2 py-1 rounded-full text-xs font-medium bg-gray-100 text-gray-600 whitespace-nowrap">
      <MailX className="w-3 h-3" /> Not Verified
    </span>
  );
};

const CompanyUserList = () => {
  const navigate = useNavigate();
  const { user: currentUser } = useContext(AuthContext);
  
  const formatRole = (role) => {
    const roleMap = {
      'admin': 'Admin',
      'user': 'User',
      'general_contractor': 'User',
      'end_user': 'User'
    };
    return roleMap[role] || 'User';
  };

  const copyToClipboard = (text, label) => {
    navigator.clipboard.writeText(text).then(() => {
      toast.success(`${label} copied to clipboard`);
    });
  };

  const [users, setUsers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [companyInfo, setCompanyInfo] = useState(null);
  const [pagination, setPagination] = useState({
    page: 1,
    limit: 25,
    total: 0,
    totalPages: 0
  });
  
  const [search, setSearch] = useState('');
  const [roleFilter, setRoleFilter] = useState('');
  const [statusFilter, setStatusFilter] = useState('active');
  const [searchInput, setSearchInput] = useState('');
  
  const [selectedUsers, setSelectedUsers] = useState([]);
  const [selectAll, setSelectAll] = useState(false);
  
  const [expandedRow, setExpandedRow] = useState(null);
  const [editingUser, setEditingUser] = useState(null);
  const [originalUser, setOriginalUser] = useState(null);
  
  const [newPassword, setNewPassword] = useState('');
  const [confirmPassword, setConfirmPassword] = useState('');
  
  // Auth events state
  const [userAuthEvents, setUserAuthEvents] = useState({});
  const [loadingAuthEvents, setLoadingAuthEvents] = useState({});
  const [showAllEventsModal, setShowAllEventsModal] = useState(null);
  const [allEventsData, setAllEventsData] = useState({ events: [], total: 0, page: 1, totalPages: 0 });
  const [loadingAllEvents, setLoadingAllEvents] = useState(false);
  
  const [confirmModal, setConfirmModal] = useState({
    isOpen: false,
    title: '',
    message: '',
    type: 'warning',
    onConfirm: () => {},
    confirmText: 'Confirm',
    cancelText: 'Cancel',
    showCancel: true
  });

  const loadUsers = useCallback(async () => {
    setLoading(true);
    try {
      const token = localStorage.getItem('token');
      const params = new URLSearchParams({
        page: pagination.page,
        limit: pagination.limit,
        ...(search && { search }),
        ...(roleFilter && { role: roleFilter }),
        ...(statusFilter && { filter: statusFilter })
      });

      // Use company-admin endpoint (multi-tenant secure)
      const response = await axios.get(`${BACKEND_URL}/api/company-admin/users?${params}`, {
        headers: { 'Authorization': `Bearer ${token}` }
      });

      setUsers(response.data.users || []);
      setCompanyInfo(response.data.company);
      setPagination(prev => ({
        ...prev,
        page: response.data.pagination.page,
        limit: response.data.pagination.limit,
        total: response.data.pagination.total,
        totalPages: response.data.pagination.totalPages
      }));
    } catch (error) {
      console.error('Error loading users:', error);
      if (error.response?.status === 403) {
        toast.error('Access denied. Company admin access only.');
      } else {
        toast.error('Failed to load users');
      }
    } finally {
      setLoading(false);
    }
  }, [pagination.page, pagination.limit, search, roleFilter, statusFilter]);

  useEffect(() => {
    loadUsers();
  }, [loadUsers]);

  const handleSearch = () => {
    setSearch(searchInput);
    setPagination(prev => ({ ...prev, page: 1 }));
  };

  const handlePageChange = (newPage) => {
    setPagination(prev => ({ ...prev, page: newPage }));
  };

  const handleViewToggle = async (userId) => {
    if (expandedRow === userId) {
      setExpandedRow(null);
    } else {
      setExpandedRow(userId);
      if (!userAuthEvents[userId]) {
        await loadUserAuthEvents(userId);
      }
    }
  };

  // Load last 5 auth events for a user
  const loadUserAuthEvents = async (userId) => {
    setLoadingAuthEvents(prev => ({ ...prev, [userId]: true }));
    try {
      const token = localStorage.getItem('token');
      const response = await axios.get(`${BACKEND_URL}/api/company-admin/users/${userId}/auth-events?limit=5`, {
        headers: { 'Authorization': `Bearer ${token}` }
      });
      setUserAuthEvents(prev => ({ ...prev, [userId]: response.data.events || [] }));
    } catch (error) {
      console.error('Error loading user auth events:', error);
      if (error.response?.status === 403) {
        toast.error('Access denied. User is not in your company.');
      }
      setUserAuthEvents(prev => ({ ...prev, [userId]: [] }));
    } finally {
      setLoadingAuthEvents(prev => ({ ...prev, [userId]: false }));
    }
  };

  // Load all auth events for modal
  const loadAllUserEvents = async (userId, page = 1) => {
    setLoadingAllEvents(true);
    try {
      const token = localStorage.getItem('token');
      const response = await axios.get(`${BACKEND_URL}/api/company-admin/users/${userId}/auth-events?page=${page}&pageSize=20`, {
        headers: { 'Authorization': `Bearer ${token}` }
      });
      setAllEventsData(response.data);
    } catch (error) {
      console.error('Error loading all user auth events:', error);
      toast.error('Failed to load events');
    } finally {
      setLoadingAllEvents(false);
    }
  };

  const handleShowAllEvents = async (userId) => {
    setShowAllEventsModal(userId);
    await loadAllUserEvents(userId);
  };

  // Admin actions using company-admin endpoints
  const handleForceLogout = async (userId, userEmail) => {
    setConfirmModal({
      isOpen: true,
      title: 'Force Logout User',
      message: `All sessions for ${userEmail} will be terminated. Do you want to continue?`,
      type: 'warning',
      confirmText: 'Force Logout',
      cancelText: 'Cancel',
      showCancel: true,
      onConfirm: async () => {
        try {
          const token = localStorage.getItem('token');
          await axios.post(`${BACKEND_URL}/api/company-admin/users/${userId}/force-logout`, {}, {
            headers: { 'Authorization': `Bearer ${token}` }
          });
          toast.success('User has been logged out');
          await loadUserAuthEvents(userId);
        } catch (error) {
          console.error('Error forcing logout:', error);
          toast.error(error.response?.data?.detail || 'Failed to force logout');
        } finally {
          setConfirmModal(prev => ({ ...prev, isOpen: false }));
        }
      }
    });
  };

  const handleFreezeUser = async (userId, userEmail) => {
    setConfirmModal({
      isOpen: true,
      title: 'Freeze Account',
      message: `${userEmail}'s account will be frozen and they will not be able to log in. Do you want to continue?`,
      type: 'warning',
      confirmText: 'Freeze',
      cancelText: 'Cancel',
      showCancel: true,
      onConfirm: async () => {
        try {
          const token = localStorage.getItem('token');
          await axios.post(`${BACKEND_URL}/api/company-admin/users/${userId}/freeze`, {}, {
            headers: { 'Authorization': `Bearer ${token}` }
          });
          toast.success('User account has been frozen');
          loadUsers();
          await loadUserAuthEvents(userId);
        } catch (error) {
          console.error('Error freezing user:', error);
          toast.error(error.response?.data?.detail || 'Failed to freeze account');
        } finally {
          setConfirmModal(prev => ({ ...prev, isOpen: false }));
        }
      }
    });
  };

  const handleUnfreezeUser = async (userId, userEmail) => {
    setConfirmModal({
      isOpen: true,
      title: 'Unfreeze Account',
      message: `${userEmail}'s account will be unfrozen and they will be able to log in again. Do you want to continue?`,
      type: 'warning',
      confirmText: 'Unfreeze',
      cancelText: 'Cancel',
      showCancel: true,
      onConfirm: async () => {
        try {
          const token = localStorage.getItem('token');
          await axios.post(`${BACKEND_URL}/api/company-admin/users/${userId}/unfreeze`, {}, {
            headers: { 'Authorization': `Bearer ${token}` }
          });
          toast.success('User account has been unfrozen');
          loadUsers();
          await loadUserAuthEvents(userId);
        } catch (error) {
          console.error('Error unfreezing user:', error);
          toast.error('Failed to unfreeze account');
        } finally {
          setConfirmModal(prev => ({ ...prev, isOpen: false }));
        }
      }
    });
  };

  const handleUnsuspendUser = async (userId, userEmail) => {
    setConfirmModal({
      isOpen: true,
      title: 'Unsuspend Account',
      message: `${userEmail}'s account will be unsuspended and they will be able to log in again. Do you want to continue?`,
      type: 'warning',
      confirmText: 'Unsuspend',
      cancelText: 'Cancel',
      showCancel: true,
      onConfirm: async () => {
        try {
          const token = localStorage.getItem('token');
          await axios.post(`${BACKEND_URL}/api/company-admin/users/${userId}/unsuspend`, {}, {
            headers: { 'Authorization': `Bearer ${token}` }
          });
          toast.success('User account has been unsuspended');
          loadUsers();
          await loadUserAuthEvents(userId);
        } catch (error) {
          console.error('Error unsuspending user:', error);
          toast.error('Failed to unsuspend account');
        } finally {
          setConfirmModal(prev => ({ ...prev, isOpen: false }));
        }
      }
    });
  };

  // Event type helpers
  const getEventTypeConfig = (eventType) => {
    const configs = {
      LOGIN_SUCCESS: { label: 'Login Success', icon: LogIn, color: 'text-green-600', bg: 'bg-green-100' },
      LOGIN_FAIL: { label: 'Login Failed', icon: AlertTriangle, color: 'text-red-600', bg: 'bg-red-100' },
      LOGOUT: { label: 'Logout', icon: LogOut, color: 'text-blue-600', bg: 'bg-blue-100' },
      SESSION_EXPIRED: { label: 'Session Expired', icon: Clock, color: 'text-yellow-600', bg: 'bg-yellow-100' },
      ADMIN_FORCE_LOGOUT: { label: 'Force Logout', icon: UserX, color: 'text-orange-600', bg: 'bg-orange-100' },
      ADMIN_FREEZE_USER: { label: 'Frozen', icon: Ban, color: 'text-red-600', bg: 'bg-red-100' },
      ADMIN_UNFREEZE_USER: { label: 'Unfrozen', icon: UserCheck, color: 'text-green-600', bg: 'bg-green-100' },
      ACCOUNT_SUSPENDED: { label: 'Suspended', icon: ShieldAlert, color: 'text-purple-600', bg: 'bg-purple-100' },
      ADMIN_UNSUSPEND_USER: { label: 'Unsuspended', icon: ShieldCheck, color: 'text-teal-600', bg: 'bg-teal-100' }
    };
    return configs[eventType] || { label: eventType, icon: Shield, color: 'text-gray-600', bg: 'bg-gray-100' };
  };

  const formatEventDate = (dateStr) => {
    if (!dateStr) return '-';
    try {
      const date = new Date(dateStr);
      return date.toLocaleString('en-US', { 
        day: '2-digit', month: '2-digit', year: 'numeric',
        hour: '2-digit', minute: '2-digit'
      });
    } catch {
      return dateStr;
    }
  };

  const handleEdit = (user) => {
    setEditingUser({ ...user });
    setOriginalUser(JSON.parse(JSON.stringify(user)));
  };

  const hasChanges = () => {
    if (!editingUser || !originalUser) return false;
    return JSON.stringify(editingUser) !== JSON.stringify(originalUser) || newPassword || confirmPassword;
  };

  const handleCloseEdit = () => {
    if (hasChanges()) {
      setConfirmModal({
        isOpen: true,
        title: 'Unsaved Changes',
        message: 'You have unsaved changes. Are you sure you want to close?',
        type: 'warning',
        confirmText: 'Close Anyway',
        cancelText: 'Keep Editing',
        onConfirm: () => {
          setEditingUser(null);
          setOriginalUser(null);
          setNewPassword('');
          setConfirmPassword('');
        }
      });
    } else {
      setEditingUser(null);
      setOriginalUser(null);
      setNewPassword('');
      setConfirmPassword('');
    }
  };

  const handleSaveEdit = async () => {
    if (!editingUser) return;

    // Validate password if being changed
    if (newPassword || confirmPassword) {
      if (newPassword !== confirmPassword) {
        toast.error('Passwords do not match');
        return;
      }
      if (newPassword.length < 6) {
        toast.error('Password must be at least 6 characters');
        return;
      }
    }

    try {
      const token = localStorage.getItem('token');
      const updateData = {
        firstName: editingUser.firstName,
        lastName: editingUser.lastName,
        role: editingUser.role,
        ...(newPassword && { password: newPassword })
      };

      await axios.put(`${BACKEND_URL}/api/company/users/${editingUser.id}`, updateData, {
        headers: { 'Authorization': `Bearer ${token}` }
      });

      toast.success('User updated successfully');
      setEditingUser(null);
      setOriginalUser(null);
      setNewPassword('');
      setConfirmPassword('');
      loadUsers();
    } catch (error) {
      console.error('Error updating user:', error);
      toast.error(error.response?.data?.detail || 'Failed to update user');
    }
  };

  const handleDeleteUser = async (userId, userName) => {
    setConfirmModal({
      isOpen: true,
      title: 'Delete User',
      message: `Are you sure you want to delete ${userName}? This action cannot be undone.`,
      type: 'warning',
      confirmText: 'Delete',
      cancelText: 'Cancel',
      showCancel: true,
      onConfirm: async () => {
        try {
          const token = localStorage.getItem('token');
          await axios.delete(`${BACKEND_URL}/api/company/users/${userId}`, {
            headers: { 'Authorization': `Bearer ${token}` }
          });
          toast.success('User deleted successfully');
          loadUsers();
        } catch (error) {
          console.error('Error deleting user:', error);
          toast.error(error.response?.data?.detail || 'Failed to delete user');
        } finally {
          setConfirmModal(prev => ({ ...prev, isOpen: false }));
        }
      }
    });
  };

  // Selection handlers
  const handleSelectAll = () => {
    if (selectAll) {
      setSelectedUsers([]);
    } else {
      setSelectedUsers(users.map(u => u.id));
    }
    setSelectAll(!selectAll);
  };

  const handleSelectUser = (userId) => {
    if (selectedUsers.includes(userId)) {
      setSelectedUsers(selectedUsers.filter(id => id !== userId));
    } else {
      setSelectedUsers([...selectedUsers, userId]);
    }
  };

  return (
    <CompanyAdminLayout>
      <div className="p-6 max-w-7xl mx-auto">
        {/* Header with Company Info */}
        <div className="flex items-center justify-between mb-6">
          <div>
            <h1 className="text-2xl font-bold text-gray-900 flex items-center gap-2">
              <UsersIcon className="w-7 h-7 text-orange-500" />
              Company Users
            </h1>
            {companyInfo && (
              <p className="text-gray-600 mt-1">
                Managing users for <span className="font-semibold text-orange-600">{companyInfo.name}</span>
              </p>
            )}
          </div>
          <button
            onClick={() => navigate('/company-admin/create-user')}
            className="flex items-center gap-2 px-4 py-2 bg-orange-500 text-white rounded-lg hover:bg-orange-600 transition-colors"
          >
            <UserPlus className="w-5 h-5" />
            Add User
          </button>
        </div>

        {/* Filters */}
        <div className="bg-white rounded-xl shadow-sm border border-gray-200 p-4 mb-6">
          <div className="flex flex-wrap gap-4 items-center">
            {/* Search */}
            <div className="flex-1 min-w-[200px]">
              <div className="relative">
                <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 w-5 h-5 text-gray-400" />
                <input
                  type="text"
                  value={searchInput}
                  onChange={(e) => setSearchInput(e.target.value)}
                  onKeyPress={(e) => e.key === 'Enter' && handleSearch()}
                  placeholder="Search by name or email..."
                  className="w-full pl-10 pr-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-orange-500 focus:border-transparent"
                />
              </div>
            </div>

            {/* Role Filter */}
            <div className="w-40">
              <select
                value={roleFilter}
                onChange={(e) => {
                  setRoleFilter(e.target.value);
                  setPagination(prev => ({ ...prev, page: 1 }));
                }}
                className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-orange-500 focus:border-transparent"
              >
                <option value="">All Roles</option>
                <option value="admin">Admin</option>
                <option value="user">User</option>
              </select>
            </div>

            {/* Status Filter */}
            <div className="w-40">
              <select
                value={statusFilter}
                onChange={(e) => {
                  setStatusFilter(e.target.value);
                  setPagination(prev => ({ ...prev, page: 1 }));
                }}
                className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-orange-500 focus:border-transparent"
              >
                <option value="active">Active</option>
                <option value="frozen">Frozen</option>
                <option value="suspended">Suspended</option>
                <option value="deleted">Deleted</option>
              </select>
            </div>

            {/* Search Button */}
            <button
              onClick={handleSearch}
              className="px-6 py-3 bg-orange-500 text-white rounded-lg hover:bg-orange-600 transition-colors"
            >
              Search
            </button>

            {/* Clear Filters */}
            {(searchInput || roleFilter || statusFilter !== 'active') && (
              <button
                onClick={() => {
                  setSearchInput('');
                  setSearch('');
                  setRoleFilter('');
                  setStatusFilter('active');
                  setPagination(prev => ({ ...prev, page: 1 }));
                }}
                className="px-4 py-3 border border-gray-300 text-gray-700 rounded-lg hover:bg-gray-50 transition-colors flex items-center gap-2"
              >
                <X className="w-4 h-4" />
                Clear
              </button>
            )}
          </div>
        </div>

        {/* Users Table */}
        <div className="bg-white rounded-xl shadow-sm border border-gray-200 overflow-hidden">
          <div className="overflow-x-auto">
            <table className="w-full">
              <thead className="bg-gray-50 border-b border-gray-200">
                <tr>
                  <th className="px-4 py-3 text-left">
                    <Checkbox
                      checked={selectAll}
                      onCheckedChange={handleSelectAll}
                    />
                  </th>
                  <th className="px-4 py-3 text-left text-xs font-semibold text-gray-600 uppercase tracking-wider">
                    User
                  </th>
                  <th className="px-4 py-3 text-left text-xs font-semibold text-gray-600 uppercase tracking-wider whitespace-nowrap">
                    Email Verification
                  </th>
                  <th className="px-4 py-3 text-left text-xs font-semibold text-gray-600 uppercase tracking-wider">
                    Role
                  </th>
                  <th className="px-4 py-3 text-left text-xs font-semibold text-gray-600 uppercase tracking-wider">
                    Status
                  </th>
                  <th className="px-4 py-3 text-left text-xs font-semibold text-gray-600 uppercase tracking-wider">
                    Created
                  </th>
                  <th className="px-4 py-3 text-right text-xs font-semibold text-gray-600 uppercase tracking-wider">
                    Actions
                  </th>
                </tr>
              </thead>
              <tbody className="divide-y divide-gray-100">
                {loading ? (
                  <tr>
                    <td colSpan="7" className="px-4 py-12 text-center">
                      <RefreshCw className="w-8 h-8 text-orange-500 animate-spin mx-auto mb-2" />
                      <p className="text-gray-500">Loading users...</p>
                    </td>
                  </tr>
                ) : users.length === 0 ? (
                  <tr>
                    <td colSpan="7" className="px-4 py-12 text-center">
                      <UsersIcon className="w-12 h-12 text-gray-300 mx-auto mb-2" />
                      <p className="text-gray-500">No users found</p>
                    </td>
                  </tr>
                ) : (
                  users.map((user) => (
                    <React.Fragment key={user.id}>
                      <tr className={`hover:bg-gray-50 transition-colors ${expandedRow === user.id ? 'bg-orange-50' : ''}`}>
                        <td className="px-4 py-3">
                          <Checkbox
                            checked={selectedUsers.includes(user.id)}
                            onCheckedChange={() => handleSelectUser(user.id)}
                          />
                        </td>
                        <td className="px-4 py-3">
                          <div className="flex items-center gap-3">
                            <div className="w-10 h-10 rounded-full bg-orange-100 flex items-center justify-center">
                              <User className="w-5 h-5 text-orange-600" />
                            </div>
                            <div>
                              <div className="font-medium text-gray-900">
                                {user.firstName} {user.lastName}
                              </div>
                              <div className="text-sm text-gray-500">{user.email}</div>
                            </div>
                          </div>
                        </td>
                        <td className="px-4 py-3" data-testid={`user-email-verification-${user.id}`}>
                          <EmailVerificationBadge status={getEmailVerificationStatus(user)} />
                        </td>
                        <td className="px-4 py-3">
                          <span className={`inline-flex items-center px-2.5 py-1 rounded-full text-xs font-medium ${
                            user.role === 'admin' 
                              ? 'bg-purple-100 text-purple-700' 
                              : 'bg-gray-100 text-gray-700'
                          }`}>
                            {formatRole(user.role)}
                          </span>
                        </td>
                        <td className="px-4 py-3">
                          {user.status === 'FROZEN' ? (
                            <span className="inline-flex items-center gap-1 px-2 py-1 rounded-full text-xs font-medium bg-red-100 text-red-700">
                              <Ban className="w-3 h-3" /> Frozen
                            </span>
                          ) : user.status === 'SUSPENDED' ? (
                            <span className="inline-flex items-center gap-1 px-2 py-1 rounded-full text-xs font-medium bg-purple-100 text-purple-700">
                              <ShieldAlert className="w-3 h-3" /> Suspended
                            </span>
                          ) : (
                            <span className="inline-flex items-center gap-1 px-2 py-1 rounded-full text-xs font-medium bg-green-100 text-green-700">
                              <UserCheck className="w-3 h-3" /> Active
                            </span>
                          )}
                        </td>
                        <td className="px-4 py-3 text-sm text-gray-500">
                          {new Date(user.createdAt).toLocaleDateString()}
                        </td>
                        <td className="px-4 py-3">
                          <div className="flex items-center justify-end gap-2">
                            <button
                              onClick={() => handleViewToggle(user.id)}
                              className="p-2 text-gray-400 hover:text-orange-600 hover:bg-orange-50 rounded-lg transition-colors"
                              title="View Details"
                            >
                              <Eye className="w-4 h-4" />
                            </button>
                            <button
                              onClick={() => handleEdit(user)}
                              className="p-2 text-gray-400 hover:text-blue-600 hover:bg-blue-50 rounded-lg transition-colors"
                              title="Edit"
                            >
                              <Edit className="w-4 h-4" />
                            </button>
                            {user.id !== currentUser?.id && user.role !== 'admin' && (
                              <button
                                onClick={() => handleDeleteUser(user.id, `${user.firstName} ${user.lastName}`)}
                                className="p-2 text-gray-400 hover:text-red-600 hover:bg-red-50 rounded-lg transition-colors"
                                title="Delete"
                              >
                                <Trash2 className="w-4 h-4" />
                              </button>
                            )}
                          </div>
                        </td>
                      </tr>
                      
                      {/* Expanded Row */}
                      {expandedRow === user.id && (
                        <tr>
                          <td colSpan="7" className="px-4 py-4 bg-orange-50">
                            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
                              {/* User Info */}
                              <div className="bg-white rounded-lg p-4 border border-orange-200">
                                <h4 className="font-semibold text-gray-900 mb-3 flex items-center gap-2">
                                  <User className="w-4 h-4 text-orange-500" />
                                  User Details
                                </h4>
                                <dl className="space-y-2 text-sm">
                                  <div className="flex justify-between">
                                    <dt className="text-gray-500">Email:</dt>
                                    <dd className="text-gray-900 flex items-center gap-1">
                                      {user.email}
                                      <button onClick={() => copyToClipboard(user.email, 'Email')} className="text-gray-400 hover:text-orange-500">
                                        <Copy className="w-3 h-3" />
                                      </button>
                                    </dd>
                                  </div>
                                  <div className="flex justify-between">
                                    <dt className="text-gray-500">ID:</dt>
                                    <dd className="text-gray-900 font-mono text-xs flex items-center gap-1">
                                      {user.id.substring(0, 8)}...
                                      <button onClick={() => copyToClipboard(user.id, 'User ID')} className="text-gray-400 hover:text-orange-500">
                                        <Copy className="w-3 h-3" />
                                      </button>
                                    </dd>
                                  </div>
                                  <div className="flex justify-between">
                                    <dt className="text-gray-500">Projects:</dt>
                                    <dd className="text-gray-900">{user.projectCount || 0}</dd>
                                  </div>
                                </dl>
                              </div>

                              {/* Auth Events */}
                              <div className="bg-white rounded-lg p-4 border border-purple-200">
                                <div className="flex items-center justify-between mb-3">
                                  <h4 className="font-semibold text-gray-900 flex items-center gap-2">
                                    <Shield className="w-4 h-4 text-purple-500" />
                                    Last 5 Security Events
                                  </h4>
                                  <button
                                    onClick={() => handleShowAllEvents(user.id)}
                                    className="text-xs text-purple-600 hover:text-purple-800 underline"
                                  >
                                    All Events
                                  </button>
                                </div>
                                
                                {loadingAuthEvents[user.id] ? (
                                  <div className="flex items-center justify-center py-4">
                                    <RefreshCw className="w-5 h-5 text-purple-500 animate-spin" />
                                  </div>
                                ) : userAuthEvents[user.id]?.length > 0 ? (
                                  <div className="space-y-2">
                                    {userAuthEvents[user.id].map((event, idx) => {
                                      const config = getEventTypeConfig(event.eventType);
                                      const Icon = config.icon;
                                      return (
                                        <div key={idx} className="flex items-center gap-2 text-xs">
                                          <div className={`p-1 rounded ${config.bg}`}>
                                            <Icon className={`w-3 h-3 ${config.color}`} />
                                          </div>
                                          <span className="font-medium text-gray-700">{config.label}</span>
                                          <span className="text-gray-400 ml-auto">{formatEventDate(event.occurredAt)}</span>
                                        </div>
                                      );
                                    })}
                                  </div>
                                ) : (
                                  <p className="text-sm text-gray-500 py-2">No events yet</p>
                                )}
                              </div>

                              {/* Session Management */}
                              {user.id !== currentUser?.id && user.role !== 'admin' && (
                                <div className="bg-white rounded-lg p-4 border border-orange-200">
                                  <h4 className="font-semibold text-gray-900 mb-3 flex items-center gap-2">
                                    <Shield className="w-4 h-4 text-orange-500" />
                                    Session & Access
                                  </h4>
                                  <div className="space-y-2">
                                    <button
                                      onClick={() => handleForceLogout(user.id, user.email)}
                                      className="w-full flex items-center justify-center gap-2 px-3 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 transition-colors text-sm"
                                    >
                                      <LogOut className="w-4 h-4" />
                                      Force Logout
                                    </button>
                                    
                                    {user.status === 'FROZEN' ? (
                                      <button
                                        onClick={() => handleUnfreezeUser(user.id, user.email)}
                                        className="w-full flex items-center justify-center gap-2 px-3 py-2 bg-green-600 text-white rounded-lg hover:bg-green-700 transition-colors text-sm"
                                      >
                                        <UserCheck className="w-4 h-4" />
                                        Unfreeze Account
                                      </button>
                                    ) : user.status === 'SUSPENDED' ? (
                                      <button
                                        onClick={() => handleUnsuspendUser(user.id, user.email)}
                                        className="w-full flex items-center justify-center gap-2 px-3 py-2 bg-teal-600 text-white rounded-lg hover:bg-teal-700 transition-colors text-sm"
                                      >
                                        <ShieldCheck className="w-4 h-4" />
                                        Unsuspend Account
                                      </button>
                                    ) : (
                                      <button
                                        onClick={() => handleFreezeUser(user.id, user.email)}
                                        className="w-full flex items-center justify-center gap-2 px-3 py-2 bg-red-600 text-white rounded-lg hover:bg-red-700 transition-colors text-sm"
                                      >
                                        <Ban className="w-4 h-4" />
                                        Freeze Account
                                      </button>
                                    )}
                                  </div>
                                </div>
                              )}
                            </div>
                          </td>
                        </tr>
                      )}
                    </React.Fragment>
                  ))
                )}
              </tbody>
            </table>
          </div>

          {/* Pagination */}
          {!loading && pagination.totalPages > 1 && (
            <div className="px-4 py-3 border-t border-gray-200 flex items-center justify-between bg-gray-50">
              <div className="text-sm text-gray-600">
                Showing {((pagination.page - 1) * pagination.limit) + 1} to {Math.min(pagination.page * pagination.limit, pagination.total)} of {pagination.total} users
              </div>
              <div className="flex items-center gap-2">
                <button
                  onClick={() => handlePageChange(pagination.page - 1)}
                  disabled={pagination.page === 1}
                  className="p-2 rounded-lg border border-gray-300 hover:bg-gray-100 disabled:opacity-50 disabled:cursor-not-allowed"
                >
                  <ChevronLeft className="w-4 h-4" />
                </button>
                <span className="text-sm text-gray-700">
                  Page {pagination.page} / {pagination.totalPages}
                </span>
                <button
                  onClick={() => handlePageChange(pagination.page + 1)}
                  disabled={pagination.page === pagination.totalPages}
                  className="p-2 rounded-lg border border-gray-300 hover:bg-gray-100 disabled:opacity-50 disabled:cursor-not-allowed"
                >
                  <ChevronRight className="w-4 h-4" />
                </button>
              </div>
            </div>
          )}
        </div>

        {/* Edit User Modal */}
        {editingUser && (
          <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
            <div className="bg-white rounded-xl shadow-2xl w-full max-w-md">
              <div className="p-6 border-b border-gray-200 flex items-center justify-between">
                <h3 className="text-lg font-bold text-gray-900">Edit User</h3>
                <button onClick={handleCloseEdit} className="text-gray-400 hover:text-gray-600">
                  <X className="w-5 h-5" />
                </button>
              </div>
              <div className="p-6 space-y-4">
                <div className="grid grid-cols-2 gap-4">
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">First Name</label>
                    <input
                      type="text"
                      value={editingUser.firstName || ''}
                      onChange={(e) => setEditingUser({ ...editingUser, firstName: e.target.value })}
                      className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-orange-500"
                    />
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">Last Name</label>
                    <input
                      type="text"
                      value={editingUser.lastName || ''}
                      onChange={(e) => setEditingUser({ ...editingUser, lastName: e.target.value })}
                      className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-orange-500"
                    />
                  </div>
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">Role</label>
                  <select
                    value={editingUser.role || 'user'}
                    onChange={(e) => setEditingUser({ ...editingUser, role: e.target.value })}
                    className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-orange-500"
                    disabled={editingUser.role === 'admin'}
                  >
                    <option value="user">User</option>
                    <option value="admin">Admin</option>
                  </select>
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">New Password (optional)</label>
                  <PasswordInput
                    value={newPassword}
                    onChange={(e) => setNewPassword(e.target.value)}
                    placeholder="Leave empty to keep current"
                    className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-orange-500"
                  />
                </div>
                {newPassword && (
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">Confirm Password</label>
                    <PasswordInput
                      value={confirmPassword}
                      onChange={(e) => setConfirmPassword(e.target.value)}
                      className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-orange-500"
                    />
                  </div>
                )}
              </div>
              <div className="p-6 border-t border-gray-200 flex justify-end gap-3">
                <button
                  onClick={handleCloseEdit}
                  className="px-4 py-2 text-gray-700 border border-gray-300 rounded-lg hover:bg-gray-50"
                >
                  Cancel
                </button>
                <button
                  onClick={handleSaveEdit}
                  className="px-4 py-2 bg-orange-500 text-white rounded-lg hover:bg-orange-600"
                >
                  Save Changes
                </button>
              </div>
            </div>
          </div>
        )}

        {/* All Events Modal */}
        {showAllEventsModal && (
          <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
            <div className="bg-white rounded-xl shadow-2xl w-full max-w-2xl max-h-[80vh] overflow-hidden">
              <div className="p-6 border-b border-gray-200 flex items-center justify-between bg-purple-50">
                <h3 className="text-lg font-bold text-purple-900 flex items-center gap-2">
                  <Shield className="w-5 h-5" />
                  All Security Events
                </h3>
                <button onClick={() => setShowAllEventsModal(null)} className="text-gray-400 hover:text-gray-600">
                  <X className="w-5 h-5" />
                </button>
              </div>
              
              <div className="p-6 overflow-y-auto max-h-[60vh]">
                {loadingAllEvents ? (
                  <div className="flex items-center justify-center py-8">
                    <RefreshCw className="w-8 h-8 text-purple-500 animate-spin" />
                  </div>
                ) : allEventsData.events?.length > 0 ? (
                  <div className="space-y-3">
                    {allEventsData.events.map((event, idx) => {
                      const config = getEventTypeConfig(event.eventType);
                      const Icon = config.icon;
                      return (
                        <div key={idx} className="flex items-center gap-3 text-sm bg-gray-50 rounded-lg px-4 py-3 border border-gray-100">
                          <div className={`p-2 rounded-lg ${config.bg}`}>
                            <Icon className={`w-5 h-5 ${config.color}`} />
                          </div>
                          <div className="flex-1">
                            <div className="font-medium text-gray-900">{config.label}</div>
                            <div className="text-xs text-gray-500 mt-0.5">
                              IP: {event.ipAddress || '-'}
                            </div>
                          </div>
                          <span className="text-gray-500 text-xs">
                            {formatEventDate(event.occurredAt)}
                          </span>
                        </div>
                      );
                    })}
                  </div>
                ) : (
                  <p className="text-center text-gray-500 py-8">No events yet</p>
                )}
              </div>
              
              {allEventsData.totalPages > 1 && (
                <div className="p-4 border-t border-gray-200 flex items-center justify-between bg-gray-50">
                  <div className="text-sm text-gray-600">
                    Total {allEventsData.total} records
                  </div>
                  <div className="flex items-center gap-2">
                    <button
                      onClick={() => loadAllUserEvents(showAllEventsModal, allEventsData.page - 1)}
                      disabled={allEventsData.page === 1}
                      className="p-2 rounded-lg border border-gray-300 hover:bg-gray-100 disabled:opacity-50 disabled:cursor-not-allowed"
                    >
                      <ChevronLeft className="w-4 h-4" />
                    </button>
                    <span className="text-sm text-gray-700">
                      Page {allEventsData.page} / {allEventsData.totalPages}
                    </span>
                    <button
                      onClick={() => loadAllUserEvents(showAllEventsModal, allEventsData.page + 1)}
                      disabled={allEventsData.page === allEventsData.totalPages}
                      className="p-2 rounded-lg border border-gray-300 hover:bg-gray-100 disabled:opacity-50 disabled:cursor-not-allowed"
                    >
                      <ChevronRight className="w-4 h-4" />
                    </button>
                  </div>
                </div>
              )}
            </div>
          </div>
        )}

        {/* Confirmation Modal */}
        <ConfirmationModal
          isOpen={confirmModal.isOpen}
          onClose={() => setConfirmModal({ ...confirmModal, isOpen: false })}
          onConfirm={confirmModal.onConfirm}
          title={confirmModal.title}
          message={confirmModal.message}
          type={confirmModal.type}
          confirmText={confirmModal.confirmText}
          cancelText={confirmModal.cancelText}
          showCancel={confirmModal.showCancel}
        />
      </div>
    </CompanyAdminLayout>
  );
};

export default CompanyUserList;
